import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Box, Paper, Typography } from '@mui/material';

import { DocumentDirectionField } from '../../config/DocumentDirection';
import { dataGridPremiumColumns } from '../../utils/helper';
import { CheckboxUI, DataGridIdentifiers, DatagridPremiumUi } from '../Interface';

const DocumentSeriesDocTypeTable = ({ documentTypes, viewField }) => {
  const { values, setFieldValue } = useFormikContext();
  const links = useMemo(() => values.documentSeriesDocType ?? [], [values.documentSeriesDocType]);

  const rows = useMemo(() => {
    const typesById = new Map(documentTypes.map((item) => [Number(item.documentTypeId), item]));
    links.forEach((link) => {
      if (link.documentType) typesById.set(Number(link.docTypeId), link.documentType);
    });
    return [...typesById.values()].sort((left, right) => (left.name ?? '').localeCompare(right.name ?? '', 'hu'));
  }, [documentTypes, links]);

  const findLink = (docTypeId) => links.find((item) => Number(item.docTypeId) === Number(docTypeId));

  const setAssigned = (docTypeId, assigned) => {
    const existing = findLink(docTypeId);
    if (assigned && !existing) {
      setFieldValue('documentSeriesDocType', [
        ...links,
        { documentSeriesId: values.id ?? undefined, docTypeId: Number(docTypeId), isDefault: false },
      ]);
    } else if (!assigned && existing) {
      setFieldValue(
        'documentSeriesDocType',
        links.filter((item) => Number(item.docTypeId) !== Number(docTypeId))
      );
    }
  };

  const setDefault = (docTypeId, isDefault) => {
    setFieldValue(
      'documentSeriesDocType',
      links.map((item) =>
        Number(item.docTypeId) === Number(docTypeId) ? { ...item, isDefault: Boolean(isDefault) } : item
      )
    );
  };

  const columns = dataGridPremiumColumns([
    { field: 'documentTypeId', headerName: '#', type: 'number', width: 80 },
    { field: 'name', headerName: 'Bizonylattípus', type: 'string', minWidth: 240, flex: 1 },
    {
      field: 'direction',
      headerName: 'Irány',
      type: 'string',
      minWidth: 120,
      valueGetter: (value) => DocumentDirectionField.find((item) => item.value === value)?.label ?? value,
    },
    {
      field: 'assigned',
      headerName: 'Hozzárendelve',
      type: 'boolean',
      sortable: false,
      filterable: false,
      minWidth: 130,
      valueGetter: (_value, row) => Boolean(findLink(row.documentTypeId)),
      renderCell: (params) => (
        <CheckboxUI
          name={`documentSeriesDocType_${params.row.documentTypeId}`}
          checked={Boolean(findLink(params.row.documentTypeId))}
          disabled={viewField || values.generated}
          size="small"
          onChange={(event) => setAssigned(params.row.documentTypeId, event.target.checked)}
        />
      ),
    },
    {
      field: 'isDefault',
      headerName: 'Alapértelmezett',
      type: 'boolean',
      sortable: false,
      filterable: false,
      minWidth: 130,
      valueGetter: (_value, row) => Boolean(findLink(row.documentTypeId)?.isDefault),
      renderCell: (params) => {
        const link = findLink(params.row.documentTypeId);
        return (
          <CheckboxUI
            name={`documentSeriesDocTypeDefault_${params.row.documentTypeId}`}
            checked={Boolean(link?.isDefault)}
            disabled={viewField || values.generated || !link || !values.active}
            size="small"
            onChange={(event) => setDefault(params.row.documentTypeId, event.target.checked)}
          />
        );
      },
    },
  ]);

  return (
    <Box sx={{ mx: 2, mb: 2 }}>
      <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: { xs: 1, sm: 2 } }}>
        <Typography variant="body2" color="text.secondary" fontWeight={600} fontSize="1rem" my={1} px={2}>
          Bizonylattípusok
        </Typography>
        <DatagridPremiumUi
          datagridIdentifier={DataGridIdentifiers.DocumentSeriesDocTypes}
          disableSave
          height={420}
          columns={columns}
          rows={rows}
          rowCount={rows.length}
          getRowId={(row) => row.documentTypeId}
          hideFooter
          disableRowSelectionOnClick
          onCellDoubleClick={(_params, event) => {
            event.defaultMuiPrevented = true;
          }}
        />
      </Paper>
    </Box>
  );
};

export default DocumentSeriesDocTypeTable;
